import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Switch,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { FiCopy, FiEdit2, FiPlus } from 'react-icons/fi';

import {
  apiErrorMessage,
  Feed,
  feedAnalysisService,
  FeedFormula,
  FeedReference,
  REFERENCED_NUTRIENTS,
  ReferencedNutrientKey,
} from '../../../../services/feedAnalysisService';
import { currentMonth, formatNumber, monthInput, monthShort } from '../format';
import { PageShell } from '../PageShell';

const tableBox = {
  bg: 'gray.800',
  borderWidth: '1px',
  borderColor: 'gray.700',
  borderRadius: 'lg',
  overflowX: 'auto' as const,
};

const formBox = {
  bg: 'gray.800',
  borderWidth: '1px',
  borderColor: 'blue.500',
  borderRadius: 'lg',
  p: '4',
  mb: '4',
};

function toNumber(value: string): number | null {
  const text = value.trim();
  if (!text) return null;
  const number = Number(
    text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text,
  );
  return Number.isFinite(number) ? number : null;
}

const toText = (value: number | null | undefined) =>
  value === null || value === undefined ? '' : String(value).replace('.', ',');

type Notify = (error: unknown, fallback: string) => void;

// --- rações -----------------------------------------------------------------

const FeedsTab: React.FC<{
  feeds: Feed[];
  formulas: FeedFormula[];
  onSaved: () => void;
  onError: Notify;
}> = ({ feeds, formulas, onSaved, onError }) => {
  const toast = useToast();
  const [editing, setEditing] = useState<Partial<Feed> | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await feedAnalysisService.saveFeed(
        {
          name: editing.name || '',
          formula_id: editing.formula_id || null,
          active: editing.active ?? true,
          notes: editing.notes || null,
        },
        editing.id,
      );
      toast({ title: 'Ração salva.', status: 'success' });
      setEditing(null);
      onSaved();
    } catch (error) {
      onError(error, 'Não foi possível salvar a ração.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Text fontSize="sm" color="gray.400" mb="4">
        Ligue cada ração (nome usado no laudo do laboratório) à fórmula do
        nutricionista. Várias rações podem usar a mesma fórmula; ração sem
        fórmula fica sem referência.
      </Text>
      {editing ? (
        <Box {...formBox}>
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing="3" mb="3">
            <FormControl isRequired>
              <FormLabel fontSize="xs">Ração (nome no laudo)</FormLabel>
              <Input
                size="sm"
                value={editing.name || ''}
                onChange={event => {
                  setEditing({ ...editing, name: event.target.value });
                }}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Fórmula do nutricionista</FormLabel>
              <Select
                size="sm"
                placeholder="Sem fórmula"
                value={editing.formula_id || ''}
                onChange={event => {
                  setEditing({
                    ...editing,
                    formula_id: event.target.value || null,
                  });
                }}
              >
                {formulas.map(formula => (
                  <option key={formula.id} value={formula.id}>
                    {formula.code}
                    {formula.description ? ` — ${formula.description}` : ''}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Observação</FormLabel>
              <Input
                size="sm"
                value={editing.notes || ''}
                onChange={event => {
                  setEditing({ ...editing, notes: event.target.value });
                }}
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" mt={{ md: '6' }}>
              <Switch
                isChecked={editing.active ?? true}
                onChange={event => {
                  setEditing({ ...editing, active: event.target.checked });
                }}
                mr="2"
              />
              <FormLabel fontSize="sm" mb="0">
                Ativa
              </FormLabel>
            </FormControl>
          </SimpleGrid>
          <Flex gridGap="2" justify="flex-end">
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={save}
              isLoading={busy}
            >
              Salvar
            </Button>
          </Flex>
        </Box>
      ) : (
        <Button
          size="sm"
          leftIcon={<FiPlus />}
          mb="4"
          onClick={() => setEditing({ active: true })}
        >
          Nova ração
        </Button>
      )}
      <Box {...tableBox}>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Ração</Th>
              <Th>Fórmula</Th>
              <Th>Status</Th>
              <Th>Observação</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {feeds.map(feed => (
              <Tr key={feed.id}>
                <Td fontWeight="600">{feed.name}</Td>
                <Td>
                  {feed.formula_code || (
                    <Text as="span" color="yellow.300">
                      Sem fórmula
                    </Text>
                  )}
                </Td>
                <Td>
                  <Badge colorScheme={feed.active ? 'green' : 'gray'}>
                    {feed.active ? 'Ativa' : 'Inativa'}
                  </Badge>
                </Td>
                <Td>{feed.notes}</Td>
                <Td isNumeric>
                  <Button
                    size="xs"
                    variant="ghost"
                    leftIcon={<FiEdit2 />}
                    onClick={() => setEditing(feed)}
                  >
                    Editar
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </>
  );
};

// --- fórmulas ---------------------------------------------------------------

const FormulasTab: React.FC<{
  formulas: FeedFormula[];
  onSaved: () => void;
  onError: Notify;
}> = ({ formulas, onSaved, onError }) => {
  const toast = useToast();
  const [editing, setEditing] = useState<Partial<FeedFormula> | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await feedAnalysisService.saveFormula(
        {
          code: editing.code || '',
          description: editing.description || null,
          active: editing.active ?? true,
        },
        editing.id,
      );
      toast({ title: 'Fórmula salva.', status: 'success' });
      setEditing(null);
      onSaved();
    } catch (error) {
      onError(error, 'Não foi possível salvar a fórmula.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {editing ? (
        <Box {...formBox}>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing="3" mb="3">
            <FormControl isRequired>
              <FormLabel fontSize="xs">Código (nutricionista)</FormLabel>
              <Input
                size="sm"
                placeholder="Ex.: 19508-SR06"
                value={editing.code || ''}
                onChange={event => {
                  setEditing({ ...editing, code: event.target.value });
                }}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Descrição</FormLabel>
              <Input
                size="sm"
                value={editing.description || ''}
                onChange={event => {
                  setEditing({ ...editing, description: event.target.value });
                }}
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" mt={{ md: '6' }}>
              <Switch
                isChecked={editing.active ?? true}
                onChange={event => {
                  setEditing({ ...editing, active: event.target.checked });
                }}
                mr="2"
              />
              <FormLabel fontSize="sm" mb="0">
                Ativa
              </FormLabel>
            </FormControl>
          </SimpleGrid>
          <Flex gridGap="2" justify="flex-end">
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={save}
              isLoading={busy}
            >
              Salvar
            </Button>
          </Flex>
        </Box>
      ) : (
        <Button
          size="sm"
          leftIcon={<FiPlus />}
          mb="4"
          onClick={() => setEditing({ active: true })}
        >
          Nova fórmula
        </Button>
      )}
      <Box {...tableBox}>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Código</Th>
              <Th>Descrição</Th>
              <Th>Status</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {formulas.map(formula => (
              <Tr key={formula.id}>
                <Td fontWeight="600">{formula.code}</Td>
                <Td>{formula.description}</Td>
                <Td>
                  <Badge colorScheme={formula.active ? 'green' : 'gray'}>
                    {formula.active ? 'Ativa' : 'Inativa'}
                  </Badge>
                </Td>
                <Td isNumeric>
                  <Button
                    size="xs"
                    variant="ghost"
                    leftIcon={<FiEdit2 />}
                    onClick={() => setEditing(formula)}
                  >
                    Editar
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </>
  );
};

// --- referências do nutricionista -------------------------------------------

type ReferenceForm = {
  id?: string;
  formula_id: string;
  valid_from: string;
  values: Record<ReferencedNutrientKey, string>;
  tolerance_above: string; // em %
  tolerance_below: string;
  notes: string;
};

const emptyValues = () =>
  Object.fromEntries(
    REFERENCED_NUTRIENTS.map(item => [item.key, '']),
  ) as Record<ReferencedNutrientKey, string>;

const percentText = (value: number | null) =>
  value === null ? '' : toText(Math.round(value * 10000) / 100);

const ReferencesTab: React.FC<{
  references: FeedReference[];
  formulas: FeedFormula[];
  onSaved: () => void;
  onError: Notify;
}> = ({ references, formulas, onSaved, onError }) => {
  const toast = useToast();
  const [formulaFilter, setFormulaFilter] = useState('');
  const [editing, setEditing] = useState<ReferenceForm | null>(null);
  const [busy, setBusy] = useState(false);

  const fromReference = (
    reference: FeedReference,
    copy: boolean,
  ): ReferenceForm => ({
    id: copy ? undefined : reference.id,
    formula_id: reference.formula_id,
    valid_from: copy ? currentMonth() : monthInput(reference.valid_from),
    values: Object.fromEntries(
      REFERENCED_NUTRIENTS.map(item => [item.key, toText(reference[item.key])]),
    ) as Record<ReferencedNutrientKey, string>,
    tolerance_above: percentText(reference.tolerance_above),
    tolerance_below: percentText(reference.tolerance_below),
    notes: copy ? '' : reference.notes || '',
  });

  const save = async () => {
    if (!editing) return;
    const percent = (value: string) => {
      const number = toNumber(value);
      return number === null ? null : number / 100;
    };
    setBusy(true);
    try {
      await feedAnalysisService.saveReference(
        {
          formula_id: editing.formula_id,
          valid_from: editing.valid_from,
          ...(Object.fromEntries(
            REFERENCED_NUTRIENTS.map(item => [
              item.key,
              toNumber(editing.values[item.key]),
            ]),
          ) as Record<ReferencedNutrientKey, number | null>),
          tolerance_above: percent(editing.tolerance_above),
          tolerance_below: percent(editing.tolerance_below),
          notes: editing.notes || null,
        },
        editing.id,
      );
      toast({ title: 'Referência salva.', status: 'success' });
      setEditing(null);
      onSaved();
    } catch (error) {
      onError(error, 'Não foi possível salvar a referência.');
    } finally {
      setBusy(false);
    }
  };

  const visible = formulaFilter
    ? references.filter(item => item.formula_id === formulaFilter)
    : references;

  return (
    <>
      <Text fontSize="sm" color="gray.400" mb="4">
        Uma linha por fórmula e mês de vigência. Quando o nutricionista mudar
        uma fórmula, cadastre uma nova vigência (não edite a antiga): cada mês
        usa a vigência mais recente até ele. Tolerância vazia = padrão.
      </Text>
      {editing ? (
        <Box {...formBox}>
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing="3" mb="3">
            <FormControl isRequired>
              <FormLabel fontSize="xs">Fórmula</FormLabel>
              <Select
                size="sm"
                placeholder="Selecione"
                value={editing.formula_id}
                onChange={event => {
                  setEditing({ ...editing, formula_id: event.target.value });
                }}
              >
                {formulas.map(formula => (
                  <option key={formula.id} value={formula.id}>
                    {formula.code}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="xs">Mês de vigência</FormLabel>
              <Input
                size="sm"
                type="month"
                value={editing.valid_from}
                onChange={event => {
                  setEditing({ ...editing, valid_from: event.target.value });
                }}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Tolerância − (%)</FormLabel>
              <Input
                size="sm"
                inputMode="decimal"
                placeholder="padrão"
                value={editing.tolerance_below}
                onChange={event => {
                  setEditing({
                    ...editing,
                    tolerance_below: event.target.value,
                  });
                }}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Tolerância + (%)</FormLabel>
              <Input
                size="sm"
                inputMode="decimal"
                placeholder="padrão"
                value={editing.tolerance_above}
                onChange={event => {
                  setEditing({
                    ...editing,
                    tolerance_above: event.target.value,
                  });
                }}
              />
            </FormControl>
          </SimpleGrid>
          <SimpleGrid columns={{ base: 2, md: 4, xl: 7 }} spacing="3" mb="3">
            {REFERENCED_NUTRIENTS.map(nutrient => (
              <FormControl key={nutrient.key}>
                <FormLabel fontSize="xs" isTruncated>
                  {nutrient.label} (%)
                </FormLabel>
                <Input
                  size="sm"
                  inputMode="decimal"
                  value={editing.values[nutrient.key]}
                  onChange={event => {
                    setEditing({
                      ...editing,
                      values: {
                        ...editing.values,
                        [nutrient.key]: event.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            ))}
          </SimpleGrid>
          <FormControl mb="3">
            <FormLabel fontSize="xs">Observação</FormLabel>
            <Input
              size="sm"
              placeholder="Ex.: enviado pelo nutricionista em 26/08"
              value={editing.notes}
              onChange={event => {
                setEditing({ ...editing, notes: event.target.value });
              }}
            />
          </FormControl>
          <Flex gridGap="2" justify="flex-end">
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={save}
              isLoading={busy}
            >
              Salvar
            </Button>
          </Flex>
        </Box>
      ) : (
        <Flex gridGap="3" mb="4" flexWrap="wrap">
          <Button
            size="sm"
            leftIcon={<FiPlus />}
            onClick={() => {
              setEditing({
                formula_id: formulaFilter,
                valid_from: currentMonth(),
                values: emptyValues(),
                tolerance_above: '',
                tolerance_below: '',
                notes: '',
              });
            }}
          >
            Nova vigência
          </Button>
          <Select
            size="sm"
            w="260px"
            value={formulaFilter}
            onChange={event => setFormulaFilter(event.target.value)}
          >
            <option value="">Todas as fórmulas</option>
            {formulas.map(formula => (
              <option key={formula.id} value={formula.id}>
                {formula.code}
              </option>
            ))}
          </Select>
        </Flex>
      )}
      <Box {...tableBox}>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Fórmula</Th>
              <Th>Vigência</Th>
              {REFERENCED_NUTRIENTS.map(nutrient => (
                <Th key={nutrient.key} isNumeric>
                  {nutrient.label}
                </Th>
              ))}
              <Th>Tolerância</Th>
              <Th>Observação</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {visible.map(reference => (
              <Tr key={reference.id}>
                <Td fontWeight="600" whiteSpace="nowrap">
                  {reference.formula_code}
                </Td>
                <Td>{monthShort(reference.valid_from)}</Td>
                {REFERENCED_NUTRIENTS.map(nutrient => (
                  <Td key={nutrient.key} isNumeric color="blue.300">
                    {formatNumber(reference[nutrient.key])}
                  </Td>
                ))}
                <Td whiteSpace="nowrap">
                  {reference.tolerance_below === null &&
                  reference.tolerance_above === null
                    ? 'padrão'
                    : `-${
                        percentText(reference.tolerance_below) || 'padrão'
                      }% / +${
                        percentText(reference.tolerance_above) || 'padrão'
                      }%`}
                </Td>
                <Td fontSize="xs" color="gray.400">
                  {reference.notes}
                </Td>
                <Td isNumeric whiteSpace="nowrap">
                  <Button
                    size="xs"
                    variant="ghost"
                    leftIcon={<FiEdit2 />}
                    onClick={() => setEditing(fromReference(reference, false))}
                  >
                    Editar
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    leftIcon={<FiCopy />}
                    title="Nova vigência com estes valores"
                    onClick={() => setEditing(fromReference(reference, true))}
                  >
                    Nova vigência
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </>
  );
};

// --- tolerância padrão ------------------------------------------------------

const ToleranceTab: React.FC<{ isAdmin: boolean; onError: Notify }> = ({
  isAdmin,
  onError,
}) => {
  const toast = useToast();
  const [above, setAbove] = useState('');
  const [below, setBelow] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    feedAnalysisService
      .getSettings()
      .then(settings => {
        setAbove(percentText(settings.default_tolerance_above));
        setBelow(percentText(settings.default_tolerance_below));
      })
      .catch(error =>
        onError(error, 'Não foi possível carregar a tolerância.'),
      );
  }, [onError]);

  const save = async () => {
    const aboveValue = toNumber(above);
    const belowValue = toNumber(below);
    if (aboveValue === null || belowValue === null) return;
    setBusy(true);
    try {
      await feedAnalysisService.updateSettings({
        default_tolerance_above: aboveValue / 100,
        default_tolerance_below: belowValue / 100,
      });
      toast({ title: 'Tolerância padrão salva.', status: 'success' });
    } catch (error) {
      onError(error, 'Não foi possível salvar a tolerância.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box {...tableBox} p="4" maxW="520px">
      <Text fontSize="sm" color="gray.400" mb="4">
        Vale para todas as fórmulas que não têm tolerância própria. Faixa ideal
        = esperado − tolerância até esperado + tolerância.
      </Text>
      <SimpleGrid columns={2} spacing="3" mb="4">
        <FormControl>
          <FormLabel fontSize="xs">Para menos (%)</FormLabel>
          <Input
            size="sm"
            inputMode="decimal"
            value={below}
            onChange={event => setBelow(event.target.value)}
            isDisabled={!isAdmin}
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs">Para mais (%)</FormLabel>
          <Input
            size="sm"
            inputMode="decimal"
            value={above}
            onChange={event => setAbove(event.target.value)}
            isDisabled={!isAdmin}
          />
        </FormControl>
      </SimpleGrid>
      <FormControl>
        <FormHelperText mb="3">
          {isAdmin
            ? 'Mudar a tolerância reavalia todas as amostras, inclusive as antigas.'
            : 'Só o administrador muda a tolerância padrão.'}
        </FormHelperText>
      </FormControl>
      {isAdmin && (
        <Button size="sm" colorScheme="blue" onClick={save} isLoading={busy}>
          Salvar
        </Button>
      )}
    </Box>
  );
};

const FeedAnalysisSettings: React.FC = () => {
  const toast = useToast();
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [formulas, setFormulas] = useState<FeedFormula[]>([]);
  const [references, setReferences] = useState<FeedReference[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);

  const onError = useCallback<Notify>(
    (error, fallback) =>
      toast({ title: apiErrorMessage(error, fallback), status: 'error' }),
    [toast],
  );

  const load = useCallback(async () => {
    try {
      const [feedList, formulaList, referenceList] = await Promise.all([
        feedAnalysisService.listFeeds(),
        feedAnalysisService.listFormulas(),
        feedAnalysisService.listReferences(),
      ]);
      setFeeds(feedList);
      setFormulas(formulaList);
      setReferences(referenceList);
    } catch (error) {
      onError(error, 'Não foi possível carregar os cadastros.');
    }
  }, [onError]);

  useEffect(() => {
    load();
    feedAnalysisService
      .me()
      .then(access => setIsAdmin(access.isAdmin))
      .catch(() => setIsAdmin(false));
  }, [load]);

  const activeFormulas = useMemo(
    () => formulas.filter(item => item.active),
    [formulas],
  );

  return (
    <PageShell
      title="Cadastros"
      subtitle="Rações, fórmulas e valores de referência do nutricionista."
    >
      <Tabs variant="soft-rounded" colorScheme="blue" isLazy>
        <TabList flexWrap="wrap" gridGap="2" mb="4">
          <Tab>Rações ({feeds.length})</Tab>
          <Tab>Fórmulas ({formulas.length})</Tab>
          <Tab>Referências ({references.length})</Tab>
          <Tab>Tolerância padrão</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px="0">
            <FeedsTab
              feeds={feeds}
              formulas={activeFormulas}
              onSaved={load}
              onError={onError}
            />
          </TabPanel>
          <TabPanel px="0">
            <FormulasTab formulas={formulas} onSaved={load} onError={onError} />
          </TabPanel>
          <TabPanel px="0">
            <ReferencesTab
              references={references}
              formulas={activeFormulas}
              onSaved={load}
              onError={onError}
            />
          </TabPanel>
          <TabPanel px="0">
            <ToleranceTab isAdmin={isAdmin} onError={onError} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </PageShell>
  );
};

export default FeedAnalysisSettings;
